import { createContext, useContext, useEffect, useMemo, useState } from "react";
import jwtDecode from "jwt-decode";

import config from "../config";

const ACCESS_TOKEN_STORAGE_KEY = "accessToken";

const AuthContext = createContext({
  isAuthenticated: false,
  isBootstrapping: true,
  userId: null,
  login: () => {},
  logout: () => {},
});

const getStoredToken = () => localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);

const getUserIdFromToken = (token) => {
  if (!token) {
    return null;
  }

  try {
    const decodedToken = jwtDecode(token);
    return decodedToken?.id ?? decodedToken?.userId ?? null;
  } catch (error) {
    console.error("Impossible de decoder le token d'authentification.", error);
    return null;
  }
};

const isTokenExpired = (token) => {
  if (!token) {
    return true;
  }

  try {
    const decodedToken = jwtDecode(token);

    if (!decodedToken?.exp) {
      return false;
    }

    return decodedToken.exp * 1000 <= Date.now();
  } catch (error) {
    return true;
  }
};

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userId, setUserId] = useState(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);

  useEffect(() => {
    const storedToken = getStoredToken();
    const storedUserId = getUserIdFromToken(storedToken);
    const tokenExpired = isTokenExpired(storedToken);

    if (storedToken && storedUserId && !tokenExpired) {
      setIsAuthenticated(true);
      setUserId(storedUserId);
      setIsBootstrapping(false);
      return;
    }

    if (process.env.NODE_ENV === "development" && config.appUser) {
      setIsAuthenticated(true);
      setUserId(config.appUser);
    }

    if (storedToken && (!storedUserId || tokenExpired)) {
      localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
    }

    setIsBootstrapping(false);
  }, []);

  const login = (accessToken, nextUserId) => {
    if (accessToken && !isTokenExpired(accessToken)) {
      localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, accessToken);
    } else {
      localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
    }

    const resolvedUserId = nextUserId ?? getUserIdFromToken(accessToken);
    const authenticated = Boolean(accessToken && resolvedUserId && !isTokenExpired(accessToken));

    setIsAuthenticated(authenticated);
    setUserId(authenticated ? resolvedUserId : null);
  };

  const logout = () => {
    localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
    setIsAuthenticated(false);
    setUserId(null);
  };

  const value = useMemo(
    () => ({
      isAuthenticated,
      isBootstrapping,
      userId,
      login,
      logout,
    }),
    [isAuthenticated, isBootstrapping, userId]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
